import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Loader2, Plus, CreditCard, Wifi, Zap, MapPin, X, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { maskCardNumber, cn } from "../lib/utils";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cardTypes = ["Visa", "Mastercard", "Verve"];

export const Cards = () => {
  const { user } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  
  const [formData, setFormData] = useState({
    cardType: "",
    deliveryAddress: user?.address || "",
  });

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const response = await axios.get(`${API_URL}/cards`);
      setCards(response.data);
      
    } catch (error) {
      console.error("Failed to fetch cards:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.cardType || !formData.deliveryAddress) {
      setError("Please fill all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/cards/apply`, {
        card_type: formData.cardType,
        delivery_address: formData.deliveryAddress,
      });
      toast.success("Card application submitted! We will reach out to you");
      setShowForm(false);
      setFormData({ cardType: "", deliveryAddress: user?.address || "" });
      fetchCards();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  const getCardDesign = (type) => {
    switch (type?.toLowerCase()) {
      case "visa":
        return "from-[#1a1f71] to-[#005792] shadow-blue-200";
      case "mastercard":
        return "from-[#eb001b] to-[#f79e1b] shadow-orange-200";
      case "verve":
        return "from-[#00a859] to-[#006837] shadow-emerald-200";
      default:
        return "from-slate-800 to-slate-950 shadow-slate-300";
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-4 animate-in fade-in slide-in-from-bottom-4 duration-700" data-testid="cards-page">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-black tracking-tight text-slate-900 uppercase">My Cards</h1>
          <p className="text-slate-500 font-bold uppercase text-[12px] tracking-[0.2em] mt-1">Manage your cards</p>
        </div>
        {!showForm && (
          <Button 
            onClick={() => setShowForm(true)} 
            className="rounded-2xl bg-slate-900 hover:bg-slate-800 h-12 px-6 font-black uppercase text-[12px] tracking-widest transition-all active:scale-95 shadow-xl shadow-slate-200"
            data-testid="apply-card-btn"
          >
            <Plus className="w-4 h-4 mr-2 stroke-[3px]" />
            Request New Card
          </Button>
        )}
      </div>

      {/* Application Form - Bento Card Style */}
      {showForm && (
        <Card className="rounded-[2.5rem] border-none shadow-2xl bg-white overflow-hidden animate-in zoom-in-95 duration-300">
          <CardHeader className="bg-slate-50/50 p-8 border-b border-slate-100 relative">
            <button 
              onClick={() => setShowForm(false)}
              className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white border border-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-colors shadow-sm"
            >
              <X size={18} />
            </button>
            <div className="flex items-center gap-3 mb-2">
              <Zap className="text-amber-500 w-5 h-5 fill-amber-500" />
              <CardTitle className="font-black text-slate-900 uppercase text-sm tracking-widest leading-none">Card Application</CardTitle>
            </div>
            <CardDescription className="text-[11px] font-bold uppercase text-slate-400 tracking-tight">Standard delivery takes 3-5 business days</CardDescription>
          </CardHeader>
          <CardContent className="p-8">
            {error && (
              <Alert variant="destructive" className="mb-6 rounded-2xl border-none bg-red-50 text-red-600">
                <AlertDescription className="font-bold text-[12px] uppercase">{error}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1 tracking-widest">Card Network</Label>
                  <Select
                    value={formData.cardType}
                    onValueChange={(v) => setFormData((p) => ({ ...p, cardType: v }))}
                  >
                    <SelectTrigger className="h-14 rounded-2xl bg-slate-50 border-none font-bold text-slate-900 focus:ring-slate-900" data-testid="card-type-select">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-none shadow-xl ">
                      {cardTypes.map((t) => (
                        <SelectItem key={t} value={t} className="font-bold text-sm">{t.toUpperCase()}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] font-black uppercase text-slate-400 ml-1 tracking-widest">Delivery Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      className="h-14 pl-11 rounded-2xl bg-slate-50 border-none font-bold text-sm text-slate-900 focus-visible:ring-slate-900"
                      value={formData.deliveryAddress}
                      onChange={(e) => setFormData((p) => ({ ...p, deliveryAddress: e.target.value }))}
                      placeholder="Street, City, Postcode"
                      data-testid="delivery-address-input"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-4 pt-2">
                <Button type="submit" className="flex-1 h-14 rounded-2xl bg-slate-900 hover:bg-slate-800 text-sm font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-200 transition-all active:scale-95" disabled={submitting} data-testid="submit-card-btn">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Confirm & Order"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Cards Display Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[1, 2].map((i) => (
            <div key={i} className="h-56 bg-slate-100 rounded-[2.5rem] animate-pulse" />
          ))}
        </div>
      ) : cards.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center bg-slate-50/50 rounded-[3rem] border-2 border-dashed border-slate-100">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 shadow-sm">
            <CreditCard className="w-8 h-8 text-slate-300" />
          </div>
          <p className="font-black text-slate-600 uppercase text-sm tracking-widest">No active cards</p>
          <p className="text-[12px] font-bold text-slate-500 uppercase tracking-tighter mt-1">
            Apply for a card to start spending
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {cards.map((card) => (
            <div
              key={card.id}
              className={cn(
                "relative h-56 rounded-[2.5rem] p-8 text-white overflow-hidden shadow-2xl transition-all hover:scale-[1.02] cursor-default bg-gradient-to-br",
                getCardDesign(card.card_type)
              )}
              data-testid={`card-${card.id}`}
            >
              {/* Premium Card Patterns */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-24 -mt-24 blur-3xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-black/10 rounded-full -ml-16 -mb-16 blur-2xl pointer-events-none" />
              
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div className="flex items-start justify-between">
                  <div className="flex flex-col">
                    <span className="font-black italic text-xl tracking-tighter">VAULT</span>
                    <span className="text-[8px] font-black uppercase tracking-[0.4em] opacity-60">Platinum Member</span>
                  </div>
                  <Wifi className="w-8 h-8 opacity-40 rotate-90" />
                </div>
                
                <div className="space-y-4">
                  <p className="text-xl font-mono tracking-[0.25em] font-medium drop-shadow-md">
                    {card.card_number}
                  </p>
                  
                  <div className="flex items-end justify-between">
                    <div className="flex gap-8">
                      <div>
                        <p className="text-[8px] font-black uppercase tracking-widest opacity-60 mb-0.5">Expiry</p>
                        <p className="font-mono text-sm font-bold tracking-widest">{card.expiry_date}</p>
                      </div>
                      <div>
                        <p className="text-[8px] font-black uppercase tracking-widest opacity-60 mb-0.5">CVV</p>
                        <p className="font-mono text-sm font-bold tracking-[0.3em]">{card.cvv}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-black uppercase text-[12px] tracking-widest mb-1">{user?.first_name} {user?.last_name}</p>
                      <div className="px-3 py-1 bg-white/20 backdrop-blur-md rounded-lg inline-block border border-white/10">
                        <span className="text-[12px] font-black uppercase tracking-tighter italic">{card.card_type}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Security Info */}
      <div className="flex items-center justify-center gap-2 pt-4 opacity-50">
        <ShieldCheck size={14} className="text-slate-400" />
        <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
          PCI-DSS Compliant • 256-Bit SSL Secured
        </p>
      </div>
    </div>
  );
};